import { useCallback } from 'react';
import { ensureTrailingEmptyFormDataRow } from '../../utils/multipart';
import { parseCurl } from '../../utils/parseCurl';
import { urlFromParams } from '../../utils/urlParams';
import { ensureTrailingEmptyKvRow } from '../../utils/kvRows';
import { normalizeAuth } from '../../utils/requestAuth';
import { useNotificationStore } from '../../store/notificationStore';
import { normalizeRequestBodyMode } from '../../utils/requestBodyMode';

/** cURL / URL paste into the URL bar, and auth updates. */
export function useRequestUrlPaste({
  t,
  folderVariables,
  initialData,
  selectedEnvironment,
  workspaceVariables,
  globalVariables,
  collectionVariables,
  currentWorkspaceId,
  onVariableUpdate,
  showToast,
  setMethod,
  setUrl,
  setUrlImportNonce,
  setHeaders,
  setParams,
  setCookies,
  setBody,
  setBodyType,
  setRawBodyLanguage,
  setFormDataFields,
  setAuth,
  notifyChange,
}) {
  const handleUrlPaste = useCallback(
    (text) => {
      const parsed = parseCurl(text);
      if (!parsed) {
        // A pasted curl we cannot read used to vanish without a word (card 7.5).
        // Say so, and keep the multi-line command out of the single-line URL field.
        if (/^\s*curl(?:\.exe)?\s/i.test(text || '')) {
          useNotificationStore
            .getState()
            .warning(t('request.curlPasteFailed'), t('request.curlPasteTitle'));
          return true;
        }
        return false;
      }

      const ts = Date.now();
      const headerRows = ensureTrailingEmptyKvRow(
        parsed.headers.length > 0
          ? parsed.headers.map((h, i) => ({
              key: h.key,
              value: h.value,
              enabled: true,
              _id: `h-${i}-${ts}`,
            }))
          : [],
        'h'
      );
      const paramRows = ensureTrailingEmptyKvRow(
        parsed.params.length > 0
          ? parsed.params.map((p, i) => ({
              key: p.key,
              value: p.value,
              enabled: true,
              _id: `p-${i}-${ts}`,
            }))
          : [],
        'p'
      );
      const cookieRows = ensureTrailingEmptyKvRow(
        parsed.cookies.length > 0
          ? parsed.cookies.map((c, i) => ({
              key: c.key,
              value: c.value,
              enabled: true,
              _id: `c-${i}-${ts}`,
            }))
          : [],
        'c'
      );

      const displayUrl = urlFromParams(parsed.url, paramRows);

      setMethod(parsed.method);
      setUrl(displayUrl);
      setUrlImportNonce((n) => n + 1);
      setHeaders(headerRows);
      setParams(paramRows);
      setCookies(cookieRows);
      setBody(parsed.body || '');
      const importedBodyMode = normalizeRequestBodyMode({ bodyType: parsed.bodyType });
      setBodyType(importedBodyMode.bodyType);
      setRawBodyLanguage(importedBodyMode.rawBodyLanguage);

      // urlencoded (--data-urlencode, or -d with that Content-Type) and -F land as
      // editor rows: the Body tab edits and sends those, not the `body` string.
      const extra = {};
      if (parsed.formFields?.length) {
        extra.formDataFields = ensureTrailingEmptyFormDataRow(
          parsed.formFields.map((f, i) => ({
            key: f.key,
            value: f.value,
            type: f.type === 'file' ? 'file' : 'text',
            enabled: true,
            filePath: null,
            _id: `fd-${i}-${ts}`,
          }))
        );
        setFormDataFields(extra.formDataFields);
      }
      if (parsed.auth) {
        extra.auth = normalizeAuth(parsed.auth, { forRequest: true });
        setAuth(extra.auth);
      }

      notifyChange({
        method: parsed.method,
        url: displayUrl,
        headers: headerRows,
        params: paramRows,
        cookies: cookieRows,
        body: parsed.body || '',
        bodyType: importedBodyMode.bodyType,
        rawBodyLanguage: importedBodyMode.rawBodyLanguage,
        ...extra,
      });

      showToast('Imported from cURL', 'success');
      if (parsed.skippedFileBody) {
        useNotificationStore
          .getState()
          .warning(t('request.curlPasteFileSkipped'), t('request.curlPasteTitle'));
      }
      return true;
    },
    [notifyChange, showToast, t, setAuth, setBody, setBodyType, setCookies, setFormDataFields, setHeaders, setMethod, setParams, setRawBodyLanguage, setUrl, setUrlImportNonce]
  );

  const authVariableProps = {
    selectedEnvironment,
    workspaceVariables,
    globalVariables,
    collectionVariables,
    folderVariables,
    onVariableUpdate,
    variableContext: { collectionId: initialData?.collectionId, workspaceId: currentWorkspaceId },
  };

  const updateAuth = (patch) => {
    setAuth((prev) => {
      const next = normalizeAuth({ ...prev, ...patch }, { forRequest: true });
      notifyChange({ auth: next });
      return next;
    });
  };

  return { handleUrlPaste, authVariableProps, updateAuth };
}
